import {
  Box, Typography, Button, Stack, TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Autocomplete, Select, MenuItem,
  FormControl, InputLabel, InputAdornment,
} from "@mui/material";
import AddIcon           from "@mui/icons-material/Add";
import EditIcon          from "@mui/icons-material/Edit";
import DeleteIcon        from "@mui/icons-material/Delete";
import ContentCopyIcon   from "@mui/icons-material/ContentCopy";
import ArrowUpwardIcon   from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon  from "@mui/icons-material/InfoOutlined";
import { useState, useEffect, useMemo } from "react";
import { startToast, updateToastError, updateToastSuccess, updateToastWarning } from "../../../utils/notification-loading";
import API from "../../../api";
import WarningDialog from "../../../components/WarningDialog";

// ── Constants ─────────────────────────────────────────────────────────────────

const SEVERITY_LEVELS = [
  { value: "critical", label: "Critical", color: "#ce2528" },
  { value: "high",     label: "High",     color: "#ff8c00" },
  { value: "medium",   label: "Medium",   color: "#daa520" },
  { value: "low",      label: "Low",      color: "#58a8ae" },
  { value: "ignore",   label: "Ignore",   color: "#6b6b6b" },
];
const SEVERITY_MAP = Object.fromEntries(SEVERITY_LEVELS.map((s) => [s.value, s]));

// The catalog's severity_values include "info", which isn't one of our 5 selectable pills —
// collapse it into "low" so a notice type's default_severity always maps to a valid pill.
const SEVERITY_ALIAS = { info: "low" };
const normalizeDefaultSeverity = (raw) => (SEVERITY_MAP[raw] ? raw : (SEVERITY_ALIAS[raw] ?? "medium"));
const SEVERITY_RANK = Object.fromEntries(SEVERITY_LEVELS.map((s, i) => [s.value, i]));
const severityRank = (raw) => SEVERITY_RANK[normalizeDefaultSeverity(raw)];

// A notice type's own default_severity is only the fallback used when no action-specific rule
// matches — its actions often carry different (usually higher) severities. Collapsing that down
// to one chip is misleading, so we surface the full distinct set instead.
function severityRangeFor(noticeType) {
  const set = new Set([normalizeDefaultSeverity(noticeType.default_severity)]);
  (noticeType.actions ?? []).forEach((a) => set.add(normalizeDefaultSeverity(a.default_severity)));
  return [...set].sort((a, b) => SEVERITY_RANK[a] - SEVERITY_RANK[b]); // most severe first
}

const DIALOG_META = {
  add:       { title: "Add Severity Rule",       Icon: AddIcon,         btn: "Add Rule" },
  edit:      { title: "Edit Severity Rule",      Icon: EditIcon,        btn: "Save Changes" },
  duplicate: { title: "Duplicate Severity Rule", Icon: ContentCopyIcon, btn: "Duplicate" },
};

// ── Protocol dropdown (fixed list — no free text, to avoid typos that silently break a rule) ──

const PROTOCOL_OPTIONS = [
  { value: "ads",          label: "ADS" },
  { value: "amqp",         label: "AMQP" },
  { value: "bacnet",       label: "BACnet" },
  { value: "c37118",       label: "C37.118" },
  { value: "codesys",      label: "CODESYS" },
  { value: "dds",          label: "DDS" },
  { value: "dlms",         label: "DLMS" },
  { value: "dnp3",         label: "DNP3" },
  { value: "dnp3_sa",      label: "DNP3 SA" },
  { value: "enip_cip",     label: "EtherNet/IP CIP" },
  { value: "fins",         label: "FINS (Omron)" },
  { value: "focas",        label: "FOCAS (Fanuc)" },
  { value: "ge",           label: "GE SRTP" },
  { value: "hart_ip",      label: "HART-IP" },
  { value: "iccp",         label: "ICCP" },
  { value: "iec104",       label: "IEC 104" },
  { value: "iec_mms",      label: "IEC MMS" },
  { value: "knxip",        label: "KNX/IP" },
  { value: "lsv2",         label: "LSV2 (Heidenhain)" },
  { value: "melsec",       label: "MELSEC (Mitsubishi)" },
  { value: "modbus",       label: "Modbus" },
  { value: "modbus_tls",   label: "Modbus TLS" },
  { value: "mqtt",         label: "MQTT" },
  { value: "onvif",        label: "ONVIF" },
  { value: "opcua",        label: "OPC-UA" },
  { value: "opcua_pubsub", label: "OPC-UA PubSub" },
  { value: "pccc",         label: "PCCC" },
  { value: "profinet",     label: "PROFINET" },
  { value: "ptp",          label: "PTP" },
  { value: "s7tcp",        label: "S7 TCP (Siemens)" },
  { value: "sparkplug",    label: "Sparkplug" },
  { value: "telnet",       label: "Telnet" },
  { value: "tftp",         label: "TFTP" },
  { value: "tsaa",         label: "TSAA" },
  { value: "umas",         label: "UMAS (Schneider)" },
  { value: "ur",           label: "UR (Universal Robots)" },
];
const PROTOCOL_LABEL_BY_VALUE = Object.fromEntries(PROTOCOL_OPTIONS.map((p) => [p.value, p.label]));
const PROTOCOL_VALUES = new Set(PROTOCOL_OPTIONS.map((p) => p.value));

// Best-effort action → protocol mapping, so picking e.g. "Stop PLC — Omron FINS" pre-fills
// the Protocol field with "fins". Derived from the action's technical key ("vendor:cmd") or,
// failing that, from the vendor named in its label — left unset when neither is conclusive
// (e.g. generic keys like "stop"/"run" or MITRE technique IDs with no single owning protocol).
const VENDOR_TO_PROTO = {
  "Siemens S7":         "s7tcp",
  "Omron FINS":         "fins",
  "Mitsubishi MELSEC":  "melsec",
  "CODESYS":            "codesys",
  "Schneider UMAS":     "umas",
  "Universal Robots":   "ur",
  "DNP3":               "dnp3",
  "IEC 60870-5-104":    "iec104",
  "Fanuc FOCAS":        "focas",
  "Heidenhain LSV2":    "lsv2",
  "ONVIF":              "onvif",
  "Allen-Bradley":      "enip_cip",
  "Telnet":             "telnet",
  "TFTP":               "tftp",
  "EtherNet/IP":        "enip_cip",
};

// OT Security events carry a raw "signature_id" that equals the catalog's event_type_id —
// used to auto-resolve the notice_type when jumping here from an event's context menu.
// AttackChain (9001-9005) is a single notice_type spanning a range, per the catalog's own note.
function findNoticeTypeByEventId(noticeTypes, id) {
  if (id === null || id === undefined || Number.isNaN(id)) return null;
  const exact = noticeTypes.find((n) => n.event_type_id === id);
  if (exact) return exact;
  if (id >= 9001 && id <= 9005) return noticeTypes.find((n) => n.event_type_id === 9001) ?? null;
  return null;
}

function protoFromAction(action) {
  if (!action) return null;
  const value = action.value ?? "";
  const colonIdx = value.indexOf(":");
  if (colonIdx > 0) {
    const prefix = value.slice(0, colonIdx).toLowerCase();
    if (PROTOCOL_VALUES.has(prefix)) return prefix;
  }
  const label = action.label ?? "";
  const dashIdx = label.lastIndexOf("—");
  if (dashIdx === -1) return null;
  const vendorText = label.slice(dashIdx + 1).trim();
  const vendor = Object.keys(VENDOR_TO_PROTO).find((v) => vendorText.startsWith(v));
  return vendor ? VENDOR_TO_PROTO[vendor] : null;
}

// ── Subnet validation (single IP normalized to /32 or /128, or CIDR as-is) ────

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const isValidIPv4 = (s) => { const m = s.match(IPV4_RE); return m ? m.slice(1).every((n) => Number(n) <= 255) : false; };
const IPV6_RE = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(:[0-9a-fA-F]{1,4}){1,6}|:(:[0-9a-fA-F]{1,4}){1,7}|::)$/;
const isValidIPv6 = (s) => IPV6_RE.test(s);

function normalizeSubnet(raw) {
  const s = raw.trim();
  if (!s) return { value: "", error: "" };
  const slash = s.lastIndexOf("/");
  if (slash !== -1) {
    const addr = s.slice(0, slash);
    const prefixStr = s.slice(slash + 1);
    const prefix = Number(prefixStr);
    if (!Number.isInteger(prefix) || prefixStr === "") return { value: s, error: "Invalid CIDR notation" };
    if (isValidIPv4(addr)) return prefix >= 0 && prefix <= 32  ? { value: s, error: "" } : { value: s, error: "Invalid IPv4 prefix (0-32)" };
    if (isValidIPv6(addr)) return prefix >= 0 && prefix <= 128 ? { value: s, error: "" } : { value: s, error: "Invalid IPv6 prefix (0-128)" };
    return { value: s, error: "Invalid CIDR address" };
  }
  if (isValidIPv4(s)) return { value: `${s}/32`,  error: "" };
  if (isValidIPv6(s)) return { value: `${s}/128`, error: "" };
  return { value: s, error: "Invalid IP or CIDR" };
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const SectionCard = ({ label, children }) => (
  <Box sx={{ mb: 2, borderRadius: 1.5, border: "1px solid #222", backgroundColor: "#0f0f0f", overflow: "hidden" }}>
    <Box sx={{ px: 2, py: 0.75, backgroundColor: "#0a0a0a", borderBottom: "1px solid #1e1e1e", borderLeft: "3px solid #449095" }}>
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: "#449095", letterSpacing: "0.12em", opacity: 0.85 }}>{label}</Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const pillSx = (selected, color) => ({
  px: 2, py: 0.5, borderRadius: 4, fontSize: "0.72rem", fontWeight: 700,
  border: `1px solid ${selected ? color : "#2a2a2a"}`,
  color: selected ? color : "#555",
  backgroundColor: selected ? `${color}18` : "transparent",
  "&:hover": { backgroundColor: `${color}12`, borderColor: color, color },
  transition: "all 0.15s",
});

const miniChipSx = { height: 20, fontSize: "0.65rem", color: "#999", borderColor: "#333" };

const SeverityChip = ({ value }) => {
  const def = SEVERITY_MAP[normalizeDefaultSeverity(value)];
  return (
    <Chip
      label={def.label} size="small"
      sx={{ height: 16, fontSize: "0.58rem", fontWeight: 700, flexShrink: 0, backgroundColor: `${def.color}22`, color: def.color, border: `1px solid ${def.color}55`, "& .MuiChip-label": { px: 0.6 } }}
    />
  );
};

// Shows the notice type's own default_severity, unless its actions span more than one severity —
// in that case shows the least severe one in the range with a "+" (varies by action) and lists
// the full range on hover, rather than implying a single severity applies everywhere.
const NoticeTypeSeverityChip = ({ noticeType }) => {
  const range = severityRangeFor(noticeType);
  if (range.length <= 1) return <SeverityChip value={noticeType.default_severity} />;
  const floor = range[range.length - 1];
  const def = SEVERITY_MAP[floor];
  return (
    <Tooltip title={`Varies by action: ${range.map((v) => SEVERITY_MAP[v].label).join(", ")}`} placement="top">
      <Chip
        label={`${def.label}+`} size="small"
        sx={{ height: 16, fontSize: "0.58rem", fontWeight: 700, flexShrink: 0, backgroundColor: `${def.color}22`, color: def.color, border: `1px solid ${def.color}55`, "& .MuiChip-label": { px: 0.6 } }}
      />
    </Tooltip>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const OtAnomalies = ({ rules, setRules, catalog, prefill, onPrefillConsumed }) => {
  // Edits accumulate locally — nothing is sent to the device until "Save Configuration" is
  // clicked, mirroring OtSettings.jsx rather than pushing one MQTT round-trip per click.
  const [localRules, setLocalRules] = useState(rules ?? []);
  useEffect(() => { setLocalRules(rules ?? []); }, [rules]);
  const isDirty = JSON.stringify(localRules) !== JSON.stringify(rules ?? []);

  const [search, setSearch]                 = useState("");
  const [severityFilter, setSeverityFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");

  const [open, setOpen]             = useState(false);
  const [mode, setMode]             = useState("add");
  const [editIndex, setEditIndex]   = useState(null);
  const [pendingDelete, setPendingDelete]   = useState(null);
  const [openDelConfirm, setOpenDelConfirm] = useState(false);

  const [noticeType, setNoticeType]   = useState(null);
  const [action, setAction]           = useState(null);
  const [proto, setProto]             = useState("");
  // True when the current Protocol value was auto-filled from the selected Action — locks the
  // field so it can't be edited into something that contradicts the action's own protocol.
  const [protoAutoLocked, setProtoAutoLocked] = useState(false);
  const [srcText, setSrcText]         = useState("");
  const [dstText, setDstText]         = useState("");
  const [msgContains, setMsgContains] = useState("");
  const [severity, setSeverity]       = useState("medium");

  // Memoized so these stay referentially stable across re-renders when catalog itself hasn't
  // changed — a fresh `?? []` array on every render would otherwise retrigger effects/memos
  // that depend on them (e.g. the prefill auto-open effect below).
  const noticeTypes  = useMemo(() => catalog?.notice_types ?? [], [catalog]);
  const categories   = useMemo(() => catalog?.categories ?? [], [catalog]);
  const categoryLabel = useMemo(() => Object.fromEntries(categories.map((c) => [c.category, c.category_label])), [categories]);
  const noticeByKey  = useMemo(() => Object.fromEntries(noticeTypes.map((n) => [n.notice_type, n])), [noticeTypes]);
  const catalogReady = !!catalog && noticeTypes.length > 0;

  // Auto-open the Add dialog, prefilled, when arriving here via "Configure this event" from the
  // OT Security page (see lecsManagement/index.jsx query-param wiring). The catalog is always
  // still loading on this component's very first mount (the parent fetches it asynchronously),
  // so we can't wait for catalogReady before telling the parent the prefill was delivered — if
  // we did, and the user navigated away from this section before the catalog resolved, the
  // still-"unconsumed" prefill would stay armed on the parent and auto-open the dialog on some
  // later, unrelated visit to this section (including plain navigation), once the catalog
  // finally loaded. So: snapshot the prefill locally and consume it from the parent immediately
  // on arrival, then resolve/open once the catalog is actually ready, using the local snapshot
  // rather than the (by-then-nulled) prop.
  const [pendingPrefill, setPendingPrefill] = useState(null);
  useEffect(() => {
    if (!prefill) return;
    setPendingPrefill(prefill);
    onPrefillConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  useEffect(() => {
    if (!pendingPrefill || !catalogReady) return;
    setPendingPrefill(null);

    const nt = findNoticeTypeByEventId(noticeTypes, pendingPrefill.signature_id);
    setNoticeType(nt);
    setAction(null);
    setProto(pendingPrefill.proto || "");
    setProtoAutoLocked(false);
    setSrcText(pendingPrefill.src || "");
    setDstText(pendingPrefill.dst || "");
    // Left blank even though the source event's log line is available — it's an exact
    // case-sensitive substring match, and the raw signature text is too specific/version-prone
    // to silently turn into a persistent matching condition here.
    setMsgContains("");
    setSeverity(nt ? normalizeDefaultSeverity(nt.default_severity) : (SEVERITY_MAP[pendingPrefill.severity] ? pendingPrefill.severity : "medium"));
    setMode("add");
    setEditIndex(null);
    setOpen(true);

    if (!nt) {
      startToast();
      updateToastWarning("Could not identify the anomaly type automatically — please select it manually");
    }
  }, [pendingPrefill, catalogReady, noticeTypes]);

  // Ordered by category, then by severity within each category (categories mix severities,
  // so there's no single "category severity" — sorting this way keeps the most urgent
  // anomalies of each category on top instead of implying one).
  const sortedNoticeTypes = useMemo(() => [...noticeTypes].sort((a, b) => {
    const ca = categoryLabel[a.category] ?? a.category, cb = categoryLabel[b.category] ?? b.category;
    if (ca !== cb) return ca.localeCompare(cb);
    const ra = severityRank(a.default_severity), rb = severityRank(b.default_severity);
    return ra !== rb ? ra - rb : (a.label ?? "").localeCompare(b.label ?? "");
  }), [noticeTypes, categoryLabel]);

  const filteredRules = useMemo(() => {
    let base = localRules.map((r, i) => ({ rule: r, idx: i }));
    if (severityFilter) base = base.filter(({ rule }) => rule.severity === severityFilter);
    if (categoryFilter) base = base.filter(({ rule }) => noticeByKey[rule.notice_type]?.category === categoryFilter);
    if (!search) return base;
    const re = new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
    return base.filter(({ rule }) => {
      const nt = noticeByKey[rule.notice_type];
      return re.test(nt?.label ?? rule.notice_type ?? "") || re.test(rule.proto ?? "") ||
        re.test(rule.src ?? "") || re.test(rule.dst ?? "") || re.test(rule.msg_contains ?? "") || re.test(rule.action ?? "");
    });
  }, [localRules, search, severityFilter, categoryFilter, noticeByKey]);

  const srcCheck = normalizeSubnet(srcText);
  const dstCheck = normalizeSubnet(dstText);
  const availableActions = useMemo(() => (
    [...(noticeType?.actions ?? [])].sort((a, b) => {
      const ra = severityRank(a.default_severity), rb = severityRank(b.default_severity);
      return ra !== rb ? ra - rb : (a.label ?? "").localeCompare(b.label ?? "");
    })
  ), [noticeType]);

  function reset() {
    setNoticeType(null); setAction(null); setProto(""); setProtoAutoLocked(false);
    setSrcText(""); setDstText(""); setMsgContains(""); setSeverity("medium");
  }

  // NOTE: named prefillFromRule (not "prefill") — this component also receives a `prefill` PROP
  // (the OT Security shortcut payload), and a same-named function declaration in this scope
  // would shadow it everywhere, silently breaking the `!prefill` guard in the effect below.
  function prefillFromRule(rule) {
    const nt = noticeByKey[rule.notice_type] ?? null;
    const act = (nt?.actions ?? []).find((a) => a.value === rule.action) ?? null;
    setNoticeType(nt);
    setAction(act);
    setProto(rule.proto ?? "");
    setProtoAutoLocked(!!act && protoFromAction(act) === (rule.proto ?? "") && !!protoFromAction(act));
    setSrcText(rule.src ?? "");
    setDstText(rule.dst ?? "");
    setMsgContains(rule.msg_contains ?? "");
    setSeverity(rule.severity ?? "medium");
  }

  function handleOpenAdd() { reset(); setEditIndex(null); setMode("add"); setOpen(true); }
  function handleOpenEdit(rule, idx) { prefillFromRule(rule); setEditIndex(idx); setMode("edit"); setOpen(true); }
  function handleOpenDuplicate(rule) { prefillFromRule(rule); setEditIndex(null); setMode("duplicate"); setOpen(true); }
  function handleClose() { setOpen(false); reset(); setMode("add"); setEditIndex(null); }

  function buildRule() {
    const rule = { notice_type: noticeType.notice_type, severity };
    if (proto.trim())       rule.proto = proto.trim();
    if (action)              rule.action = action.value;
    if (srcText.trim())     rule.src = srcCheck.value;
    if (dstText.trim())     rule.dst = dstCheck.value;
    if (msgContains.trim()) rule.msg_contains = msgContains.trim();
    return rule;
  }

  const hasSrcError = !!srcText.trim() && !!srcCheck.error;
  const hasDstError = !!dstText.trim() && !!dstCheck.error;
  const canSubmit = !!noticeType && !hasSrcError && !hasDstError;

  // All mutations below only touch localRules — nothing reaches the device until Save Configuration.
  function handleSubmit() {
    if (!noticeType) { startToast(); updateToastWarning("Select an anomaly type"); return; }
    if (hasSrcError) { startToast(); updateToastWarning(srcCheck.error); return; }
    if (hasDstError) { startToast(); updateToastWarning(dstCheck.error); return; }

    const rule = buildRule();
    setLocalRules((prev) => (
      mode === "edit" && editIndex !== null
        ? prev.map((r, i) => (i === editIndex ? rule : r))
        : [...prev, rule]
    ));
    handleClose();
  }

  function handleDelete(idx) {
    setLocalRules((prev) => prev.filter((_, i) => i !== idx));
  }

  function moveRule(idx, dir) {
    const swap = idx + dir;
    if (swap < 0 || swap >= localRules.length) return;
    setLocalRules((prev) => {
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }

  function handleSaveAll() {
    startToast();
    API.setSeverityRules(localRules)
      .then(() => { setRules(localRules); updateToastSuccess("Severity rules saved"); })
      .catch(() => updateToastError("Failed to save severity rules"));
  }

  function handleDiscard() {
    setLocalRules(rules ?? []);
  }

  const { title: dlgTitle, Icon: DlgIcon, btn: dlgBtn } = DIALOG_META[mode];

  return (
    <>
      {/* ── Panel header ──────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography sx={{ fontWeight: 600, color: "#e8e8e8" }}>Anomaly Severity Rules</Typography>
          <Typography variant="caption" color="text.secondary">
            Override the severity assigned to OT anomalies based on type, protocol, source/destination or action. Rules are evaluated top to bottom — the first match wins.
          </Typography>
        </Box>
        <Tooltip title={catalogReady ? "" : "Anomaly catalog unavailable"} placement="top" disableHoverListener={catalogReady}>
          <span>
            <Button
              variant="contained" size="small" onClick={handleOpenAdd} disabled={!catalogReady}
              startIcon={<AddIcon fontSize="small" />}
              sx={{ backgroundColor: "#449095", "&:hover": { backgroundColor: "#367a7e" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" }, flexShrink: 0, ml: 2 }}
            >
              Add Rule
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!catalogReady && (
        <Box sx={{ mt: 1.5, mb: 2, p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.3)", display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#c0392b" }} />
          <Typography sx={{ fontSize: "0.78rem", color: "#c0392b" }}>
            The anomaly catalog is not available. Contact support to enable severity rule configuration.
          </Typography>
        </Box>
      )}

      {isDirty && (
        <Box sx={{ mt: 1.5, mb: 2, p: 1.25, borderRadius: 1.5, backgroundColor: "rgba(218,165,32,0.08)", border: "1px solid rgba(218,165,32,0.3)", display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "#daa520" }} />
          <Typography sx={{ fontSize: "0.78rem", color: "#daa520" }}>
            You have unsaved changes. Click "Save Configuration" below to apply them to the device.
          </Typography>
        </Box>
      )}

      {/* ── Search & filters ───────────────────────────────── */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5} mt={2} flexWrap="wrap" rowGap={1} sx={{ pb: 2, borderBottom: "1px solid rgba(69,144,149,0.12)" }}>
        <TextField
          size="small" label="Search rules" value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 240 }}
        />
        <FormControl size="small" sx={{ width: 220 }}>
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((c) => <MenuItem key={c.category} value={c.category}>{c.category_label}</MenuItem>)}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
          <Button size="small" onClick={() => setSeverityFilter(null)} sx={pillSx(!severityFilter, "#449095")}>All</Button>
          {SEVERITY_LEVELS.map((s) => (
            <Button key={s.value} size="small"
              onClick={() => setSeverityFilter((prev) => (prev === s.value ? null : s.value))}
              sx={pillSx(severityFilter === s.value, s.color)}
            >
              {s.label}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* ── Rule list (ordered — first match wins) ─────────── */}
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {filteredRules.length === 0 ? (
          <Typography sx={{ color: "#555", fontSize: "0.85rem", py: 4, textAlign: "center" }}>
            {severityFilter || categoryFilter || search ? "No rules match the current filter" : "No severity rules defined — anomalies use their default severity"}
          </Typography>
        ) : filteredRules.map(({ rule, idx }) => {
          const nt  = noticeByKey[rule.notice_type];
          const sev = SEVERITY_MAP[rule.severity] ?? SEVERITY_MAP.medium;
          const hasFilters = rule.proto || rule.action || rule.src || rule.dst || rule.msg_contains;
          return (
            <Box key={idx} sx={{ borderRadius: 1.5, border: "1px solid rgba(69,146,150,0.2)", backgroundColor: "rgba(69,146,150,0.06)", p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
              <Stack alignItems="center" sx={{ width: 26, flexShrink: 0 }}>
                <IconButton size="small" disabled={idx === 0} onClick={() => moveRule(idx, -1)} sx={{ p: 0.25 }}>
                  <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                </IconButton>
                <Typography sx={{ fontSize: "0.62rem", color: "#666" }}>{idx + 1}</Typography>
                <IconButton size="small" disabled={idx === localRules.length - 1} onClick={() => moveRule(idx, 1)} sx={{ p: 0.25 }}>
                  <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Stack>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" mb={0.5}>
                  <Chip label={categoryLabel[nt?.category] ?? nt?.category ?? "Unknown category"} size="small"
                    sx={{ height: 20, fontSize: "0.63rem", fontWeight: 600, backgroundColor: "rgba(69,144,149,0.15)", color: "#7de8ed" }} />
                  <Chip label={sev.label} size="small"
                    sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, backgroundColor: `${sev.color}22`, color: sev.color, border: `1px solid ${sev.color}55` }} />
                </Stack>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#d0d0d0" }}>
                  {nt?.label ?? rule.notice_type}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" mt={0.5} useFlexGap>
                  {rule.proto        && <Chip variant="outlined" size="small" label={`proto: ${PROTOCOL_LABEL_BY_VALUE[rule.proto] ?? rule.proto}`} sx={miniChipSx} />}
                  {rule.action       && <Chip variant="outlined" size="small" label={`action: ${nt?.actions?.find((a) => a.value === rule.action)?.label ?? rule.action}`} sx={miniChipSx} />}
                  {rule.src          && <Chip variant="outlined" size="small" label={`src: ${rule.src}`} sx={{ ...miniChipSx, fontFamily: "monospace" }} />}
                  {rule.dst          && <Chip variant="outlined" size="small" label={`dst: ${rule.dst}`} sx={{ ...miniChipSx, fontFamily: "monospace" }} />}
                  {rule.msg_contains && <Chip variant="outlined" size="small" label={`msg: "${rule.msg_contains}"`} sx={miniChipSx} />}
                  {!hasFilters && <Typography sx={{ fontSize: "0.72rem", color: "#555", fontStyle: "italic" }}>Applies to every occurrence of this anomaly</Typography>}
                </Stack>
              </Box>

              <Stack direction="row" spacing={0.25}>
                <Tooltip title="Edit" placement="top">
                  <IconButton size="small" onClick={() => handleOpenEdit(rule, idx)} sx={{ p: 0.5, color: "#449095", "&:hover": { backgroundColor: "rgba(69,144,149,0.12)" } }}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplicate" placement="top">
                  <IconButton size="small" onClick={() => handleOpenDuplicate(rule)} sx={{ p: 0.5, color: "#666", "&:hover": { backgroundColor: "rgba(255,255,255,0.06)", color: "#aaa" } }}>
                    <ContentCopyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete" placement="top">
                  <IconButton size="small" onClick={() => { setPendingDelete(idx); setOpenDelConfirm(true); }} sx={{ p: 0.5, color: "#c0392b", "&:hover": { backgroundColor: "rgba(192,57,43,0.1)" } }}>
                    <DeleteIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {/* ── Global save ─────────────────────────────────────── */}
      <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
        <Button
          variant="contained" onClick={handleSaveAll} disabled={!isDirty}
          sx={{ backgroundColor: "#449095", color: "#fff", fontWeight: 700, px: 4, "&:hover": { backgroundColor: "#3a7a7e" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" } }}
        >
          Save Configuration
        </Button>
        {isDirty && (
          <Button variant="outlined" onClick={handleDiscard}
            sx={{ color: "#888", borderColor: "rgba(255,255,255,0.12)", "&:hover": { borderColor: "#666", backgroundColor: "rgba(255,255,255,0.04)", color: "#bbb" } }}>
            Discard Changes
          </Button>
        )}
      </Stack>

      {/* ── Delete confirmation ────────────────────────────── */}
      <WarningDialog
        open={openDelConfirm} setOpen={setOpenDelConfirm}
        setCheck={(val) => { setOpenDelConfirm(false); if (val && pendingDelete !== null) { handleDelete(pendingDelete); setPendingDelete(null); } }}
        message="This severity rule will be permanently removed from the device."
      />

      {/* ── Rule form dialog ───────────────────────────────── */}
      <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}
        PaperProps={{ sx: { backgroundColor: "#141414", border: "1px solid #252525", borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e1e1e", py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5}>
            <DlgIcon sx={{ color: "#449095", fontSize: 22 }} />
            <Typography fontSize={17} fontWeight={700} letterSpacing="0.01em">{dlgTitle}</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 0, marginTop: 2, marginBottom: 2 }}>
          <Stack spacing={0}>
            <SectionCard label="ANOMALY TYPE">
              <Autocomplete
                size="small"
                options={sortedNoticeTypes}
                groupBy={(opt) => categoryLabel[opt.category] ?? opt.category}
                getOptionLabel={(opt) => opt.label ?? opt.notice_type}
                isOptionEqualToValue={(a, b) => a.notice_type === b.notice_type}
                value={noticeType}
                onChange={(_, val) => {
                  setNoticeType(val);
                  setAction(null);
                  setProtoAutoLocked(false);
                  if (val) setSeverity(normalizeDefaultSeverity(val.default_severity));
                }}
                slotProps={{
                  paper: { sx: { bgcolor: "#0d1821", border: "1px solid rgba(69,144,149,0.25)" } },
                }}
                renderInput={(params) => <TextField {...params} label="Anomaly Type" placeholder="Search by name..." />}
                renderOption={(props, opt) => {
                  const { key, ...liProps } = props;
                  return (
                    <Box component="li" key={opt.notice_type} {...liProps} sx={{ display: "block !important", py: 0.75, px: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#e8e8e8", flex: 1 }}>{opt.label}</Typography>
                        <NoticeTypeSeverityChip noticeType={opt} />
                      </Stack>
                      <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)" }}>{opt.description}</Typography>
                    </Box>
                  );
                }}
              />
              {noticeType?.description && (
                <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", mt: 1 }}>{noticeType.description}</Typography>
              )}
              {noticeType?.note_for_ui && (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1, p: 1, borderRadius: 1, backgroundColor: "rgba(218,165,32,0.08)", border: "1px solid rgba(218,165,32,0.3)" }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: "#daa520", mt: 0.15 }} />
                  <Typography sx={{ fontSize: "0.72rem", color: "#daa520" }}>{noticeType.note_for_ui}</Typography>
                </Stack>
              )}

              {availableActions.length > 0 && (
                <Autocomplete
                  size="small"
                  options={availableActions}
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={action}
                  onChange={(_, val) => {
                    setAction(val);
                    setSeverity(val ? normalizeDefaultSeverity(val.default_severity) : normalizeDefaultSeverity(noticeType?.default_severity));
                    const derivedProto = protoFromAction(val);
                    if (derivedProto) { setProto(derivedProto); setProtoAutoLocked(true); }
                    else setProtoAutoLocked(false);
                  }}
                  sx={{ mt: 2 }}
                  slotProps={{ paper: { sx: { bgcolor: "#0d1821", border: "1px solid rgba(69,144,149,0.25)" } } }}
                  renderInput={(params) => <TextField {...params} label="Action" helperText="Leave empty to match any action for this anomaly type" />}
                  renderOption={(props, opt) => {
                    const { key, ...liProps } = props;
                    return (
                      <Box component="li" key={opt.value} {...liProps} sx={{ display: "flex !important", alignItems: "center", justifyContent: "space-between", gap: 1, py: 0.6, px: 1.5 }}>
                        <Typography sx={{ fontSize: "0.82rem", color: "#e8e8e8" }}>{opt.label}</Typography>
                        <SeverityChip value={opt.default_severity} />
                      </Box>
                    );
                  }}
                />
              )}
            </SectionCard>

            <SectionCard label="CONDITIONS (OPTIONAL — UNSET MEANS ANY)">
              <Stack spacing={2}>
                <Autocomplete
                  size="small"
                  disabled={protoAutoLocked}
                  options={PROTOCOL_OPTIONS}
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={PROTOCOL_OPTIONS.find((o) => o.value === proto) ?? null}
                  onChange={(_, val) => setProto(val?.value ?? "")}
                  slotProps={{ paper: { sx: { bgcolor: "#0d1821", border: "1px solid rgba(69,144,149,0.25)" } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params} label="Protocol" placeholder="Select a protocol"
                      helperText={protoAutoLocked ? "Locked — determined by the selected Action" : "Case-insensitive exact match — auto-filled when the selected Action implies a protocol"}
                    />
                  )}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth size="small" label="Source" placeholder="e.g. 192.168.1.10 or 10.0.0.0/24"
                    value={srcText} onChange={(e) => setSrcText(e.target.value)}
                    error={!!srcText.trim() && !!srcCheck.error}
                    helperText={srcText.trim() && srcCheck.error ? srcCheck.error : "Single IP or CIDR range"}
                  />
                  <TextField
                    fullWidth size="small" label="Destination" placeholder="e.g. 192.168.1.10 or 10.0.0.0/24"
                    value={dstText} onChange={(e) => setDstText(e.target.value)}
                    error={!!dstText.trim() && !!dstCheck.error}
                    helperText={dstText.trim() && dstCheck.error ? dstCheck.error : "Single IP or CIDR range"}
                  />
                </Stack>
                <TextField
                  size="small" label="Message contains" value={msgContains}
                  onChange={(e) => setMsgContains(e.target.value)}
                  helperText="Case-sensitive substring match — last resort, prefer Action when available"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          placement="top"
                          title="Matches if the anomaly's log message contains this text anywhere, exactly as typed (case-sensitive). It's a last-resort filter for anomalies that have no dedicated Action — prefer Action when the selected anomaly type offers one, since message text can change between versions."
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 17, color: "#666", cursor: "help" }} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </SectionCard>

            <SectionCard label="SEVERITY">
              {noticeType && (() => {
                // The range-floor "X+" display is only informational (it tells you actions for
                // this notice type carry different severities) — it's NOT what gets assigned to
                // `severity` when a notice type is picked (that's always its own default_severity,
                // see the Anomaly Type onChange above). So the "overriding default" check must
                // compare against the notice type's own default, not the displayed range floor,
                // or it falsely flags an override the instant a multi-severity notice type is
                // selected. Once an Action is picked, both the display AND the comparison switch
                // to that action's own default_severity, which is also what gets assigned to
                // `severity` at that point (see the Action onChange above) — so they stay in sync.
                const ownDefault = normalizeDefaultSeverity(noticeType.default_severity);
                const range = severityRangeFor(noticeType);
                const multi = !action && range.length > 1;
                const compareDefault = action ? normalizeDefaultSeverity(action.default_severity) : ownDefault;
                const displayDefault = action ? compareDefault : (multi ? range[range.length - 1] : ownDefault);
                const def = SEVERITY_MAP[displayDefault];
                return (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>
                      {action ? "Default for this action:" : "Default for this anomaly:"}
                    </Typography>
                    <Tooltip title={multi ? `Varies by action: ${range.map((v) => SEVERITY_MAP[v].label).join(", ")}` : ""} placement="top" disableHoverListener={!multi}>
                      <Chip
                        label={multi ? `${def.label}+` : def.label} size="small"
                        sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, backgroundColor: `${def.color}22`, color: def.color, border: `1px solid ${def.color}55` }}
                      />
                    </Tooltip>
                    {severity !== compareDefault && (
                      <Typography sx={{ fontSize: "0.72rem", color: "#666", fontStyle: "italic" }}>— overriding default</Typography>
                    )}
                  </Stack>
                );
              })()}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {SEVERITY_LEVELS.map((s) => (
                  <Button key={s.value} size="small" onClick={() => setSeverity(s.value)} sx={pillSx(severity === s.value, s.color)}>
                    {s.label}
                  </Button>
                ))}
              </Stack>
              {severity === "ignore" && (
                <Typography sx={{ fontSize: "0.72rem", color: "#666", mt: 1, fontStyle: "italic" }}>
                  Matching anomalies will be dropped entirely — no log, no alert.
                </Typography>
              )}
            </SectionCard>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #1e1e1e", px: 3, py: 2, justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            {mode === "edit" ? "Editing keeps this rule's position in the evaluation order." : "New rules are added at the end of the evaluation order."}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" onClick={handleClose}
              sx={{ minWidth: 90, color: "#888", borderColor: "rgba(255,255,255,0.12)", "&:hover": { borderColor: "#666", backgroundColor: "rgba(255,255,255,0.04)", color: "#bbb" } }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}
              sx={{ minWidth: 110, backgroundColor: "#449095", "&:hover": { backgroundColor: "#367a7e" }, "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" } }}>
              {dlgBtn}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OtAnomalies;
